import { FireOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Button, Checkbox, DatePicker, Input, InputNumber, Radio, Select, Tooltip } from 'antd';
import { PreSalesQuestion } from '../../../dto/model';
import { strings } from '../../../lang';
import { evaluatePreSalesQuestionValidation, evaluatePreSalesQuestionWarning, PreSalesAnswers } from '../../../utils/pre-sales-questionnaire-evaluator';
import {
  getPreSalesFaqContent,
  getPreSalesQuestionElementId,
  getPreSalesQuestionOptions,
  parsePreSalesDateAnswer,
  PRE_SALES_DATE_FORMAT,
  PreSalesQuestionType,
  toPreSalesStringArray,
} from './pre-sales-questionnaire-utils';

type PreSalesQuestionFieldProps = {
  question: PreSalesQuestion;
  answers: PreSalesAnswers;
  isTouched: boolean;
  isLocked: boolean;
  skipQualificationOnSubmit?: boolean;
  onAnswerChange: (
    questionKey: string,
    value: string | number | string[] | undefined,
    shouldEvaluate?: boolean,
  ) => void;
  onTouched: (questionKey: string) => void;
  onAnswerBlur: (questionKey: string, shouldEvaluate?: boolean) => void;
};

export const PreSalesQuestionField = ({
  question,
  answers,
  isTouched,
  isLocked,
  skipQualificationOnSubmit = false,
  onAnswerChange,
  onTouched,
  onAnswerBlur,
}: PreSalesQuestionFieldProps) => {
  const answer = answers[question.questionKey];
  const stringAnswer = typeof answer === 'string' ? answer : answer === undefined ? '' : String(answer);
  const options = getPreSalesQuestionOptions(question);
  const faqContent = getPreSalesFaqContent(question);
  const isHeatLoadQuestion = question.additionalInfo?.isHeatLoadQuestion === true;
  const validationError = evaluatePreSalesQuestionValidation(question, answer, answers, isTouched);
  const warning = validationError ? null : evaluatePreSalesQuestionWarning(question, answer, answers);
  const validationStatus = validationError ? 'error' : undefined;
  const blurTextAnswer = (nextTarget: EventTarget | null) => {
    const isMovingToSubmit = skipQualificationOnSubmit
      && nextTarget instanceof Element
      && nextTarget.closest('[data-presales-submit]') !== null;
    onAnswerBlur(question.questionKey, !isMovingToSubmit);
  };

  const renderInput = () => {
    switch (question.type) {
      case PreSalesQuestionType.Text:
        return (
          <Input
            className="bp-presales-questionnaire__control"
            value={stringAnswer}
            status={validationStatus}
            disabled={isLocked}
            onChange={(event) => onAnswerChange(question.questionKey, event.target.value)}
            onBlur={(event) => blurTextAnswer(event.relatedTarget)}
          />
        );

      case PreSalesQuestionType.Select:
        return (
          <Select
            className="bp-presales-questionnaire__control"
            value={stringAnswer || undefined}
            allowClear
            options={options}
            status={validationStatus}
            disabled={isLocked}
            onChange={(value) => {
              onAnswerChange(question.questionKey, value, true);
              onTouched(question.questionKey);
            }}
            onBlur={() => onAnswerBlur(question.questionKey)}
          />
        );

      case PreSalesQuestionType.Radio:
        return (
          <Radio.Group
            className="bp-presales-questionnaire__control bp-presales-questionnaire__radio-group"
            value={stringAnswer || undefined}
            disabled={isLocked}
            onChange={(event) => {
              onAnswerChange(question.questionKey, event.target.value, true);
              onTouched(question.questionKey);
            }}
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) {
                onAnswerBlur(question.questionKey);
              }
            }}
          >
            {options.map((option) => (
              <Radio key={option.value} value={option.value}>
                {option.label}
              </Radio>
            ))}
          </Radio.Group>
        );

      case PreSalesQuestionType.Number:
        return (
          <InputNumber
            className="bp-presales-questionnaire__control"
            value={stringAnswer === '' ? undefined : Number(stringAnswer)}
            status={validationStatus}
            disabled={isLocked}
            onChange={(value) => onAnswerChange(question.questionKey, value ?? undefined)}
            decimalSeparator=","
            onBlur={(event) => blurTextAnswer(event.relatedTarget)}
          />
        );

      case PreSalesQuestionType.Date:
        return (
          <DatePicker
            className="bp-presales-questionnaire__control"
            value={parsePreSalesDateAnswer(stringAnswer)}
            format={PRE_SALES_DATE_FORMAT}
            status={validationStatus}
            disabled={isLocked}
            onChange={(value) => onAnswerChange(question.questionKey, value?.format(PRE_SALES_DATE_FORMAT))}
            onBlur={(event) => blurTextAnswer(event.relatedTarget)}
          />
        );

      case PreSalesQuestionType.MultiSelect:
        return (
          <div className="bp-presales-questionnaire__control" onBlurCapture={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) {
              onAnswerBlur(question.questionKey);
            }
          }}>
            <Checkbox.Group
              className="bp-presales-questionnaire__checkbox-group"
              value={toPreSalesStringArray(answer)}
              options={options}
              disabled={isLocked}
              onChange={(checkedValues) => {
                onAnswerChange(question.questionKey, checkedValues.map(String), true);
                onTouched(question.questionKey);
              }}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div id={getPreSalesQuestionElementId(question.questionKey)} className="bp-presales-questionnaire__question" tabIndex={-1}>
      <span className="bp-presales-questionnaire__label-row">
        <span className="bp-presales-questionnaire__label">{question.questionText || question.questionKey}</span>
        {faqContent && (
          <Tooltip title={faqContent} placement="right">
            <Button
              className={`bp-presales-questionnaire__faq-button${isHeatLoadQuestion ? ' bp-presales-questionnaire__faq-button--heatload' : ''}`}
              size="small"
              type="text"
              disabled={isLocked}
              icon={isHeatLoadQuestion ? <FireOutlined /> : <QuestionCircleOutlined />}
              aria-label={isHeatLoadQuestion ? strings.preSalesQuestionnaire.showHeatLoadInformation : strings.preSalesQuestionnaire.showFaqInformation}
            >
              {isHeatLoadQuestion ? strings.preSalesQuestionnaire.heatLoadLabel : undefined}
            </Button>
          </Tooltip>
        )}
      </span>

      {renderInput()}
      {validationError && <span className="bp-presales-questionnaire__error">{validationError}</span>}
      {!validationError && warning && <span className="bp-presales-questionnaire__warning">{warning}</span>}
    </div>
  );
};
